/**
 * 收藏模块 V2 接口（/api/userCollect/v2）
 * 仅 src/views/collect 使用；Swagger 分组：用户收藏V2
 * 注意：全站其他页面的收藏/取消收藏仍走 V1 接口（src/api/common.api.ts），不在本文件范围内
 */
import { http } from '@/utils'

const PREFIX = '/api/userCollect/v2'

// ---------------- 基础类型 ----------------

/** 收藏标签颜色（前端字符串约定，ToolBar 使用） */
export type CollectTag = 'all' | 'red' | 'yellow' | 'green' | 'blue'
/** 时间排序：asc-正序 desc-倒序（默认倒序，即最新在前） */
export type TimeSort = 'asc' | 'desc'
/** 题目热度排序（V2 接口不支持热度排序，仅保留类型兼容 ToolBar） */
export type HeatSort = 'default' | 'asc' | 'desc'

/**
 * V2 收藏大类 label：由 /overview 接口下发（如 练习/机经/生词/电台/精听/单句），
 * 前端不枚举、原值透传（tab key、?tab=、各接口传参均以此为准）；显示名用接口的 name 字段
 */
export type CollectLabelV2 = string
/** 收藏内容类型 */
export type CollectContentType =
  | 'POST' // 机经
  | 'QUESTION' // 练习题
  | 'WORD' // 生词
  | 'RADIO' // 电台
  | 'LISTEN_THEME' // 精听主题
  | 'LISTEN_SENTENCE' // 精听单句
/** 听说读写父分类编码 */
export type CollectParentCode = 'speaking' | 'writing' | 'reading' | 'listening'
/** 统一收藏列表查询范围 */
export type CollectScope = 'ALL' | 'CATEGORY' | 'FOLDER'

/** 支持题型筛选的大类（overview 未返回 supportsQuestionType 时的前端兜底） */
export const QUESTION_TYPE_SUPPORTED_LABELS: CollectLabelV2[] = ['机经', '电台']

/**
 * 题材（genre）筛选的大类：精听素材（精听）/ 单句
 * 题型行平铺 /questionTypes 返回的所有 parents[].questionTypes，
 * 选中后 /items/page 传 genre=questionTypes[n].code（而非 questionType）
 */
export const GENRE_FILTER_LABELS: CollectLabelV2[] = ['精听', '单句']
/** 是否为题材筛选大类 */
export const isGenreFilterLabel = (label: CollectLabelV2) => GENRE_FILTER_LABELS.includes(label)

/** TypeFilter 父分类的特殊取值：选中「自建收藏夹」大类（非后端 parentCode） */
export const COLLECT_FOLDER_PARENT = 'folder'
/** 合法的听说读写父分类编码 */
export const COLLECT_PARENT_CODES: CollectParentCode[] = [
  'speaking',
  'writing',
  'reading',
  'listening'
]

/** contentType → 显示名（全部收藏列表的左侧标签） */
export const CONTENT_TYPE_NAME: Record<CollectContentType, string> = {
  POST: '题库',
  QUESTION: '练习',
  WORD: '单词',
  RADIO: '电台',
  LISTEN_THEME: '精听',
  LISTEN_SENTENCE: '单句'
}

/** 标签颜色映射：1红 2黄 3绿 4蓝（前后端约定） */
const TAG_TO_COLOR: Record<Exclude<CollectTag, 'all'>, number> = {
  red: 1,
  yellow: 2,
  green: 3,
  blue: 4
}

/** ToolBar 的 CollectTag → 接口 color 参数；'all' 返回 undefined（不传） */
export const collectTagToColor = (tag: CollectTag): number | undefined =>
  tag === 'all' ? undefined : TAG_TO_COLOR[tag]

/** ToolBar 的 TimeSort → 接口 sortOrder 参数 */
export const timeSortToOrder = (sort: TimeSort): 'ASC' | 'DESC' => (sort === 'asc' ? 'ASC' : 'DESC')

// ---------------- 响应数据结构 ----------------

/** 收藏概览（GET /overview） */
export interface CollectOverview {
  /** 今日新增总数 */
  todayTotal: number
  /** 全部收藏总数 */
  total: number
  categories: {
    label: CollectLabelV2
    name: string
    total: number
    /** 今日新增（角标） */
    todayCount: number
    /** 是否支持题型筛选（机经/电台为 true） */
    supportsQuestionType: boolean
  }[]
}

/** 可创建收藏夹的大类（GET /folderCategoryOptions） */
export interface CollectFolderCategoryOption {
  label: CollectLabelV2
  name: string
  supportsQuestionType: boolean
}

/** 自建收藏夹（GET /customFolders） */
export interface CollectFolder {
  id: number
  name: string
  /** 夹类型：1 = 默认收藏夹（永远置顶，不可移动/重命名） */
  type?: number
  /** 夹内收藏数（字段名以后端实际返回为准，做兼容兜底） */
  total?: number
  collectCount?: number
  sort?: number
}

/** 是否默认收藏夹（type=1：永远置顶，不可上下移动/重命名） */
export const isDefaultFolder = (f: CollectFolder) => f.type === 1

/** 收藏题型统计（GET /questionTypes） */
export interface CollectQuestionTypesResult {
  total: number
  parents: {
    code: CollectParentCode
    name: string
    total: number
    questionTypes: { code: string; name: string; count: number }[]
  }[]
}

/** 单词释义（WORD 使用，接口实测结构） */
export interface WordExplanation {
  /** 单词原文 */
  word?: string
  /** 词性（如 adj.） */
  type?: string
  /** 释义文本 */
  content?: string
}

/** 单词例句 / 双语（WORD 使用，接口实测结构） */
export interface WordBilingual {
  /** 单词id */
  wordId?: number
  /** 英文原句（实测字段为 source，兼容 original） */
  source?: string
  original?: string
  /** 中文译文 */
  target?: string
  /** 英文例句音频（实测字段） */
  audio?: string
}

/** 统一收藏列表项（POST /items/page，字段为接口实测结果） */
export interface CollectItem {
  collectId: number
  folderId: number
  folderName: string
  contentType: CollectContentType
  businessId: number
  title: string
  /** 题目内容（列表接口新增，item-middle 区域展示，超出省略） */
  content?: string
  /** 收藏颜色 1-4 */
  color: number
  /** 收藏时间，格式 yyyy-MM-dd HH:mm:ss */
  collectTime: string
  /** 是否可点击跳转；scope=ALL 时固定 false */
  clickable: boolean
  practiceCount: number
  canPractice: boolean
  hasRadio: boolean
  /** 题目热度 0-3（机经/练习题/电台，0 不展示） */
  hot?: number
  /** 完整度：1 完整 0 不完整（预留，与题库 TopicList 字段一致；接口暂未返回，返回后自动展示「完整」标签） */
  complete?: 1 | 0
  /** 单词释义（WORD 使用） */
  explanations?: WordExplanation[]
  /** 单词例句 / 双语（WORD 使用） */
  bilingual?: WordBilingual[]
  canListen: boolean
  /** 机经/练习题/电台；精听主题/单句关联真题时也会返回（存在时展示「去练习」按钮，同精听详情页「练真题」） */
  wpId?: number
  questionsId?: number
  parentCode?: CollectParentCode
  questionType?: string
  /** 分类 ID（机经/练习题/电台），「去练习」「听电台」跳转时透传给目标页 */
  categories?: number | string
  /** 精听主题/单句 */
  listenThemeId?: number
  /** 精听单句原文/译文 */
  original?: string
  target?: string
  /** 单词来源机经 */
  sourceWpId?: number
  sourceName?: string
  /** 单词本身（WORD） */
  word?: string
  /** 单词音标（WORD，旧字段，兼容保留） */
  ukPhonetic?: string
  usPhonetic?: string
  /** 单词音标（WORD，接口实测字段） */
  phonetic?: string
  /** 英式音标 */
  phoneticEm?: string
  /** 美式音标 */
  phoneticAm?: string
  /** 单词发音音频（默认=英式） */
  audio?: string
  /** 英式发音音频 */
  audioEm?: string
  /** 美式发音音频 */
  audioAm?: string
}

/** 统一收藏列表响应 */
export interface CollectItemsPageResult {
  /** 当前大类或收藏夹总数 */
  total: number
  /** 筛选后的数量（分页以此为准） */
  filteredTotal: number
  page: number
  perPage: number
  list: CollectItem[]
}

/** 统一收藏列表请求体 */
export interface CollectItemsPagePayload {
  scope: CollectScope
  /** scope=CATEGORY 必填；FOLDER 可传（后端校验） */
  label?: CollectLabelV2
  /** scope=FOLDER 必填 */
  folderId?: number
  parentCode?: CollectParentCode
  questionType?: string
  /** 题材筛选（精听素材/单句）：取 /questionTypes 平铺出的 questionTypes[n].code */
  genre?: string
  color?: number
  sortOrder?: 'ASC' | 'DESC'
  page: number
  /** 1-50 */
  perPage: number
}

/** V2 收藏/取消收藏请求体（toggle，本版本页面内暂不使用，封装备用） */
export interface CollectTogglePayload {
  label: CollectLabelV2
  contentType: CollectContentType
  /** 不传时使用 label 对应的系统收藏夹 */
  folderId?: number
  color?: number
  /** 统一业务ID；非单词内容可使用 */
  businessId?: number
  /** 机经ID；POST 或 RADIO 可使用 */
  wpId?: number
  /** 练习题ID；QUESTION 可使用 */
  questionsId?: number
  /** 精听主题ID */
  listenThemeId?: number
  /** 精听单句ID */
  listenSentenceId?: number
  /** 单词；contentType=WORD 时必填 */
  word?: string
  /** 来源机经ID；contentType=WORD 时必填 */
  sourceWpId?: number
}

// ---------------- 接口函数 ----------------

/** 收藏概览：全部数量、今日新增、各大类数量 */
export const getCollectOverviewApi = () =>
  http.get<CollectOverview>(`${PREFIX}/overview`, undefined, { skipMsg: true })

/** 可创建收藏夹的大类 */
export const getFolderCategoryOptionsApi = () =>
  http.get<CollectFolderCategoryOption[]>(`${PREFIX}/folderCategoryOptions`, undefined, {
    skipMsg: true
  })

/**
 * 查询某大类下的自建收藏夹（/customFolders 统一出口，其他模块请复用本函数）
 * 返回前统一处理：type=1 的默认收藏夹名称固定为「默认收藏夹」
 */
export const getCustomFoldersApi = async (label: CollectLabelV2) => {
  const res = await http.get<CollectFolder[]>(`${PREFIX}/customFolders`, { label })
  if (Array.isArray(res.data)) {
    res.data = res.data.map((f) => (isDefaultFolder(f) ? { ...f, name: '默认收藏夹' } : f))
  }
  return res
}

/** 查询题型及数量（label：机经/电台）；scope 与 items/page 一致：CATEGORY=全部/听说读写父分类，FOLDER=自建收藏夹 */
export const getCollectQuestionTypesApi = (params: {
  label: CollectLabelV2
  scope: CollectScope
  folderId?: number
  parentCode?: CollectParentCode
  color?: number
}) => http.get<CollectQuestionTypesResult>(`${PREFIX}/questionTypes`, params)

/** 统一收藏列表 */
export const getCollectItemsPageApi = (payload: CollectItemsPagePayload) =>
  http.post<CollectItemsPageResult>(`${PREFIX}/items/page`, payload)

/** 收藏/取消收藏（V2 toggle，备用；页面内取消收藏走批量删除接口） */
export const toggleCollectV2Api = (payload: CollectTogglePayload) =>
  http.post<null>(`${PREFIX}/toggle`, payload)

/** 修改收藏颜色 */
export const updateCollectColorApi = (payload: { collectId: number; color: number }) =>
  http.put<null>(`${PREFIX}/items/color`, payload)

/** 新增自建收藏夹 */
export const createCollectFolderApi = (payload: { label: CollectLabelV2; name: string }) =>
  http.post<null>(`${PREFIX}/folders`, payload)

/** 重命名自建收藏夹 */
export const renameCollectFolderApi = (id: number, name: string) =>
  http.put<null>(`${PREFIX}/folders/${id}`, { name })

/** 删除自建收藏夹（http 封装无 delete 方法，用底层 customHttp 发 DELETE） */
export const deleteCollectFolderApi = (id: number) =>
  http.customHttp<null>(`${PREFIX}/folders/${id}`, { method: 'DELETE' })

/** 自建收藏夹排序（folderIds 按目标顺序传全量自定义夹 id；不要包含默认收藏夹，否则后端静默忽略） */
export const sortCollectFoldersApi = (payload: { label: CollectLabelV2; folderIds: number[] }) =>
  http.put<null>(`${PREFIX}/folders/sort`, payload)

/** 批量移动收藏内容 */
export const moveCollectItemsApi = (payload: {
  collectIds: number[]
  sourceFolderId: number
  targetFolderId: number
}) => http.post<null>(`${PREFIX}/folders/move-items`, payload)

/** 批量删除收藏内容（deleteAll=true 时删除夹内全部） */
export const deleteCollectItemsApi = (payload: {
  folderId: number
  collectIds?: number[]
  deleteAll: boolean
}) => http.post<null>(`${PREFIX}/folders/delete-items`, payload)
